import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import SeoHead from '../../components/feature/SeoHead';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, resendVerificationEmail, loginWithLINE } = useAuth();
  const from = (location.state as { from?: string })?.from || '/';

  const [form, setForm] = useState({ email: '', password: '', remember: false });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [emailNotConfirmed, setEmailNotConfirmed] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.email.trim()) newErrors.email = '請輸入 Email';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = 'Email 格式不正確';
    if (!form.password) newErrors.password = '請輸入密碼';
    else if (form.password.length < 8) newErrors.password = '密碼至少需要 8 個字元';
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError('');
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    setLoading(true);

    const result = await login(form.email, form.password);
    
    if (!result.success) {
      if (result.error === '請先至信箱驗證 Email') {
        setEmailNotConfirmed(true);
      } else {
        setEmailNotConfirmed(false);
      }
      setServerError(result.error || '登入失敗，請稍後再試');
      setLoading(false);
      return;
    }

    navigate(from, { replace: true });
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resetEmail)) {
      setServerError('請輸入有效的 Email');
      return;
    }

    setResetLoading(true);
    setServerError('');

    try {
      const { supabase } = await import('../../lib/supabase');
      const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        setServerError(error.message);
        setResetLoading(false);
        return;
      }

      setResetSuccess(true);
      setResetLoading(false);
    } catch (error) {
      console.error('Reset password error:', error);
      setServerError('發送重設密碼郵件失敗，請稍後再試');
      setResetLoading(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setResendMessage('');
    const result = await resendVerificationEmail(form.email);
    setResendLoading(false);
    if (result.success) {
      setResendMessage('驗證信已重新寄出！');
      setResendCooldown(60);
    } else {
      setResendMessage(result.error || '寄送失敗');
    }
  };

  return (
    <div className="min-h-screen flex">
      <SeoHead
        title="登入｜藍舍AI地產"
        description="登入藍舍AI地產，AI擔任你最佳房地產助手，出租、找房、廣告、管理一手包辦。"
        canonical="/login"
        noIndex={true}
      />
      {/* Left Image */}
      <div className="hidden lg:block w-1/2 relative overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/b8cdde750979629aa1c9bba56c9691c3.jpg"
          alt="藍舍AI地產科技背景"
          className="w-full h-full object-cover object-top"
        />
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/80 via-slate-900/60 to-teal-950/70 pointer-events-none"></div>
        {/* Animated grid lines */}
        <div className="absolute inset-0 pointer-events-none" style={{backgroundImage: 'linear-gradient(rgba(20,210,180,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(20,210,180,0.07) 1px, transparent 1px)', backgroundSize: '48px 48px'}}></div>
        {/* Glow top-left */}
        <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full pointer-events-none" style={{background: 'radial-gradient(circle, rgba(20,210,180,0.18) 0%, transparent 70%)'}}></div>
        {/* Glow bottom-right */}
        <div className="absolute -bottom-20 -right-10 w-80 h-80 rounded-full pointer-events-none" style={{background: 'radial-gradient(circle, rgba(20,210,180,0.13) 0%, transparent 70%)'}}></div>

        {/* Brand */}
        <Link to="/" className="absolute top-8 left-10 flex items-center gap-2.5 text-white z-20 cursor-pointer hover:opacity-80 transition-opacity">
          <img
            src="https://public.readdy.ai/ai/img_res/d72a3498-c157-4cc6-8081-f37fa1e12754.png"
            alt="logo"
            className="h-8 w-8 object-contain"
          />
          <span className="font-bold text-lg tracking-wide">藍舍AI地產</span>
        </Link>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-start justify-center px-12 z-10">
          {/* AI badge */}
          <div className="flex items-center gap-2 mb-6 px-3.5 py-1.5 rounded-full border border-teal-400/40 bg-teal-400/10 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-teal-400 animate-pulse"></span>
            <span className="text-teal-300 text-xs font-semibold tracking-widest uppercase">AI Powered</span>
          </div>

          <h2 className="text-4xl font-black text-white leading-tight mb-3">
            AI 擔任你最佳<br />
            <span className="text-transparent bg-clip-text" style={{backgroundImage: 'linear-gradient(90deg, #2dd4bf, #34d399)'}}>房地產助手</span>
          </h2>
          <p className="text-white/60 text-sm mb-8">智能科技，讓租屋更簡單、更透明</p>

          {/* Feature tags */}
          <div className="flex flex-wrap gap-2.5">
            {['出租', '找房', '廣告', '管理', '一手包辦'].map((tag, i) => (
              <span
                key={i}
                className="px-3.5 py-1.5 rounded-full text-sm font-semibold text-teal-200 border border-teal-400/30 bg-teal-500/10 backdrop-blur-sm"
              >
                {tag}
              </span>
            ))}
          </div>

          {/* Stats row */}
          <div className="mt-12 flex gap-8">
            {[
              { value: 'AI協助', label: '廣告曝光' },
              { value: 'AI 24hr', label: '智能服務' },
              { value: '零仲介', label: '屋主直租' },
            ].map((stat, i) => (
              <div key={i}>
                <p className="text-xl font-black text-white">{stat.value}</p>
                <p className="text-white/50 text-xs mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-8 py-12 bg-white">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="flex items-center gap-2 mb-10 lg:hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/d72a3498-c157-4cc6-8081-f37fa1e12754.png"
              alt="logo"
              className="h-8 w-8 object-contain"
            />
            <span className="font-bold text-lg text-gray-900">藍舍AI地產</span>
          </Link>

          {!showForgotPassword ? (
            <>
              <h1 className="text-3xl font-black text-gray-900 mb-2">歡迎回來</h1>
              <p className="text-gray-500 text-sm mb-8">登入您的帳號，開始找房或管理房源</p>

              {serverError && (
                <div className="mb-6 px-4 py-3 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0"></i>
                    <p className="text-red-600 text-sm">{serverError}</p>
                  </div>
                  {emailNotConfirmed && (
                    <div className="pt-2 border-t border-red-100">
                      {resendMessage && (
                        <p className={`text-xs mb-2 font-medium ${resendMessage.includes('已重新') ? 'text-teal-600' : 'text-red-500'}`}>
                          {resendMessage}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendCooldown > 0 || resendLoading}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-red-200 rounded-lg text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {resendLoading ? (
                          <><i className="ri-loader-4-line animate-spin"></i>寄送中...</>
                        ) : resendCooldown > 0 ? (
                          <><i className="ri-time-line"></i>{resendCooldown}秒後可重寄</>
                        ) : (
                          <><i className="ri-mail-send-line"></i>重新寄送驗證信</>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <div className={`flex items-center gap-3 px-4 py-3 border rounded-xl transition-colors ${errors.email ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus-within:border-teal-400 focus-within:bg-white'}`}>
                    <i className="ri-mail-line text-gray-400 flex-shrink-0"></i>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="請輸入您的 Email"
                      className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400"
                    />
                  </div>
                  {errors.email && <p className="mt-1 text-xs text-red-500">{errors.email}</p>}
                </div>

                {/* Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">密碼</label>
                  <PasswordInput
                    value={form.password}
                    onChange={(v) => setForm({ ...form, password: v })}
                    placeholder="請輸入密碼（至少 8 碼）"
                    hasError={!!errors.password}
                  />
                  {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password}</p>}
                </div>

                {/* Remember & Forgot */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={form.remember}
                      onChange={(e) => setForm({ ...form, remember: e.target.checked })}
                      className="w-4 h-4 accent-teal-500 cursor-pointer"
                    />
                    <span className="text-sm text-gray-600">記住我</span>
                  </label>
                  <button 
                    type="button" 
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
                  >
                    忘記密碼？
                  </button>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-teal-500 text-white rounded-xl font-semibold text-sm hover:bg-teal-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {loading ? (
                    <><i className="ri-loader-4-line animate-spin"></i> 登入中...</>
                  ) : '登入'}
                </button>
              </form>

              {/* Divider */}
              <div className="flex items-center gap-3 my-6">
                <div className="flex-1 h-px bg-gray-200"></div>
                <span className="text-xs text-gray-400">或使用以下方式登入</span>
                <div className="flex-1 h-px bg-gray-200"></div>
              </div>

              {/* LINE Login */}
              <button
                type="button"
                onClick={loginWithLINE}
                className="w-full py-3.5 bg-[#06C755] text-white rounded-xl font-semibold text-sm hover:bg-[#05b34c] transition-colors flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <span className="font-black text-base leading-none tracking-tight">LINE</span>
                使用 LINE 帳號登入
              </button>

              <p className="mt-6 text-center text-sm text-gray-500">
                還沒有帳號？{' '}
                <Link to="/register" className="text-teal-600 font-semibold hover:text-teal-700 cursor-pointer">
                  立即註冊
                </Link>
              </p>
            </>
          ) : (
            <>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetSuccess(false);
                  setServerError('');
                }}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                <span className="text-sm">返回登入</span>
              </button>

              <h1 className="text-3xl font-black text-gray-900 mb-2">重設密碼</h1>
              <p className="text-gray-500 text-sm mb-8">輸入您的 Email，我們將發送重設密碼連結</p>

              {resetSuccess ? (
                <div className="mb-6 px-4 py-3 bg-teal-50 border border-teal-200 rounded-xl flex items-center gap-3">
                  <i className="ri-checkbox-circle-line text-teal-500 text-lg flex-shrink-0"></i>
                  <p className="text-teal-700 text-sm">重設密碼郵件已發送！請至信箱查收並點擊連結重設密碼。</p>
                </div>
              ) : (
                <>
                  {serverError && (
                    <div className="mb-6 px-4 py-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                      <i className="ri-error-warning-line text-red-500 text-lg flex-shrink-0"></i>
                      <p className="text-red-600 text-sm">{serverError}</p>
                    </div>
                  )}

                  <form onSubmit={handleForgotPassword} className="space-y-5">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                      <div className="flex items-center gap-3 px-4 py-3 border border-gray-200 bg-gray-50 rounded-xl focus-within:border-teal-400 focus-within:bg-white transition-colors">
                        <i className="ri-mail-line text-gray-400 flex-shrink-0"></i>
                        <input
                          type="email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          placeholder="請輸入您的 Email"
                          className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      disabled={resetLoading}
                      className="w-full py-3.5 bg-teal-500 text-white rounded-xl font-semibold text-sm hover:bg-teal-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      {resetLoading ? (
                        <><i className="ri-loader-4-line animate-spin"></i> 發送中...</>
                      ) : '發送重設連結'}
                    </button>
                  </form>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PasswordInput({
  value,
  onChange,
  placeholder,
  hasError,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  hasError: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className={`flex items-center gap-3 px-4 py-3 border rounded-xl transition-colors ${hasError ? 'border-red-400 bg-red-50' : 'border-gray-200 bg-gray-50 focus-within:border-teal-400 focus-within:bg-white'}`}>
      <i className="ri-lock-line text-gray-400 flex-shrink-0"></i>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 bg-transparent text-sm text-gray-800 outline-none placeholder-gray-400"
      />
      <button type="button" onClick={() => setShow(!show)} className="text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0">
        <i className={show ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
      </button>
    </div>
  );
}
